import type { WizardValidationError } from '../operateWizardHelpers';
import { getStepsForKind, type OperateWizardState, type WizardStep } from '../operateWizardReducer';

export const STEP_LABEL_KEYS: Record<WizardStep, string> = {
    kind: 'actions.operate.stepLabels.kind',
    split_continuation: 'actions.operate.stepLabels.split.continuation',
    split_targets: 'actions.operate.stepLabels.split.targets',
    merge_sources: 'actions.operate.stepLabels.merge.sources',
    merge_destination: 'actions.operate.stepLabels.merge.destination',
    sam_continuation: 'actions.operate.stepLabels.splitAndMerge.continuation',
    sam_target: 'actions.operate.stepLabels.splitAndMerge.target',
    redist_sources: 'actions.operate.stepLabels.redistribution.sources',
    redist_destinations: 'actions.operate.stepLabels.redistribution.destinations',
    bioscope_movements: 'actions.operate.stepLabels.bioscopeMovements',
    confirm: 'actions.operate.stepLabels.confirm',
};

/**
 * Filters the static step list to what the operator should actually see now:
 * the `bioscope_movements` step is dropped when the operator can't move
 * bioscopes or no source cage carries one — same machinery used by
 * `WizardFooter`'s next/back navigation so both stay in sync.
 */
export function getEffectiveSteps(
    state: OperateWizardState,
    includeBioscopeStep: boolean
): readonly WizardStep[] {
    const baseSteps = getStepsForKind(state.operationKind);
    if (includeBioscopeStep) return baseSteps;
    return baseSteps.filter((s) => s !== 'bioscope_movements');
}

/**
 * Per-step "can the operator advance?" check. Encodes the minimum data the
 * step needs in order to produce a request the backend will accept.
 */
export function stepCanProceed(
    state: OperateWizardState,
    validationErrors: WizardValidationError[]
): boolean {
    switch (state.step) {
        case 'kind':
            return state.operationKind !== null;
        case 'split_continuation': {
            // First step. If the operator kept the remainder, its name must
            // be set; if they toggled it off, the step is always passable.
            // Validation errors that pertain to the remainder still block —
            // errors on the targets (added later) do not.
            const initial = state.sources.find((s) => s.fishGroupId === state.initialSourceId);
            const sourceCageId = initial?.currentCageId ?? null;
            const remainder = state.destinations.find((d) => d.cageId === sourceCageId);
            if (!remainder) return true;
            if (remainder.name.trim().length === 0) return false;
            return !validationErrors.some(
                (err) => 'destinationTempId' in err && err.destinationTempId === remainder.tempId
            );
        }
        case 'split_targets':
            // A real split requires ≥2 destinations — otherwise the backend's
            // inferOperationType falls back to the degenerate 'merge' case.
            // This holds in both branches: continuation + ≥1 target = 2, or
            // ≥2 targets when the source cage is vacated.
            return state.destinations.length >= 2 && validationErrors.length === 0;
        case 'merge_sources':
            // Merge requires ≥2 sources (initial + at least one more).
            return state.sources.length >= 2;
        case 'merge_destination': {
            const dest = state.destinations[0];
            return Boolean(dest) && dest.cageId != null && dest.name.trim().length > 0;
        }
        case 'sam_continuation':
            // First step now: the continuation is auto-created at
            // destinations[0] on mount. Requires the cage (pinned to source)
            // and a non-empty name; the count is optional.
            return (
                state.destinations.length >= 1 &&
                Boolean(state.destinations[0]) &&
                state.destinations[0].cageId != null &&
                state.destinations[0].name.trim().length > 0 &&
                validationErrors.length === 0
            );
        case 'sam_target': {
            // Requires a chosen merge target at destinations[1] with a name
            // AND two sources total. Both branches end up with 2 sources by
            // the time the step is complete: the occupied branch adds the
            // occupant automatically when the cage is picked; the empty
            // branch surfaces an inline picker that dispatches setSecondSource.
            const target = state.destinations[1];
            return (
                Boolean(target) &&
                target.cageId != null &&
                target.name.trim().length > 0 &&
                state.sources.length >= 2
            );
        }
        case 'redist_sources':
            return state.sources.length >= 2;
        case 'redist_destinations':
            return validationErrors.length === 0 && state.destinations.length >= 2;
        case 'bioscope_movements':
            // Companion step: every bioscope defaults to "Stay" so the step
            // is always passable. The operator opts in to moves explicitly.
            return true;
        case 'confirm':
            return false;
    }
}
